import { BrowserRouter } from 'react-router-dom'
import { AppRoutes } from './router'
import ErrorBoundary from './components/ui/ErrorBoundary'
import { ThemeProvider } from './contexts/ThemeContext'
import { useEffect, useState } from 'react'
import apiService from './services/api'
import { useTranslation } from 'react-i18next'
import maintenanceService from './services/maintenance.service'
import MaintenancePage from './pages/MaintenancePage'
import authService from './services/api/auth.service'
import { useAuthStore } from './store/authStore'

function App() {
  const { i18n } = useTranslation()
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [maintenanceMessages, setMaintenanceMessages] = useState({ ar: '', en: '' })
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  useEffect(() => {
    apiService.setLanguage(i18n.language)
  }, [i18n.language])

  // ✅ On app visit: validate token with server; logout immediately if token is invalid/revoked/expired.
  useEffect(() => {
    let cancelled = false

    const decodeJwtPayload = (token: string): any | null => {
      try {
        const parts = token.split('.')
        if (parts.length !== 3) return null
        const payload = parts[1]
        let base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
        const pad = base64.length % 4
        if (pad) base64 += '='.repeat(4 - pad)
        return JSON.parse(atob(base64))
      } catch {
        return null
      }
    }

    const tokenLooksExpiredOrBroken = (token: string): boolean => {
      const payload = decodeJwtPayload(token)
      if (!payload || typeof payload.exp !== 'number') return true
      return payload.exp * 1000 <= Date.now()
    }

    const isExplicitAuthInvalid = (resp: any): boolean => {
      // We only logout on explicit auth invalid signals, not on network/500/etc.
      const code = String(resp?.code ?? resp?.data?.code ?? resp?.data?.data?.code ?? '')
      const message = String(resp?.message ?? resp?.data?.message ?? '')
      if (code === 'jwt_auth_invalid_token') return true
      if (code === 'user_not_found') return true
      if (/token\s+was\s+revoked\s+or\s+replaced/i.test(message)) return true
      if (/token\s+expired/i.test(message)) return true
      if (/user\s+not\s+found/i.test(message)) return true
      if (/jwt/i.test(code)) return true
      // Some backends only return Arabic message
      if (/انتهت\s+الجلسة|تم\s+تسجيل\s+الدخول\s+من\s+جهاز\s+آخر/i.test(message)) return true
      if (/المستخدم\s+غير\s+موجود/i.test(message)) return true
      return false
    }

    const bootstrapAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) {
          // Ensure store is logged out if token is missing
          const state = useAuthStore.getState()
          if (state?.isAuthenticated || state?.token) {
            state.logout()
          }
          return
        }

        // If token is clearly expired/broken locally, logout immediately.
        if (tokenLooksExpiredOrBroken(token)) {
          useAuthStore.getState().logout()
          return
        }

        const me = await authService.getUserInfo()
        if (me?.success && me?.data) {
          // Keep store user in sync on refresh.
          const state: any = useAuthStore.getState()
          if (typeof state.setUser === 'function') {
            state.setUser(me.data)
          }
          return
        }

        // Only logout if backend explicitly indicates auth invalid.
        if (!me?.success && isExplicitAuthInvalid(me)) {
          useAuthStore.getState().logout()
        }
      } catch {
        // Do not force logout on transient errors (network/proxy/timeout).
      } finally {
        if (!cancelled) {
          setIsCheckingAuth(false)
        }
      }
    }

    bootstrapAuth()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const status = await maintenanceService.checkMaintenance()
        setIsMaintenanceMode(status.maintenance_mode)
        setMaintenanceMessages(status.message)
      } catch {
        // On error, allow access
        setIsMaintenanceMode(false)
      } finally {
        setIsCheckingMaintenance(false)
      }
    }

    checkMaintenance()
  }, [])

  // Show loading or maintenance page
  if (isCheckingMaintenance || isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (isMaintenanceMode) {
    return <MaintenancePage messageAr={maintenanceMessages.ar} messageEn={maintenanceMessages.en} />
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
